import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  EntityType,
  EntityHistory,
  VersionComparison,
  FieldChange,
} from 'shared';
import { historyService } from '../../services/history.service';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  entityName?: string;
}

export const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState<EntityHistory[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [comparison, setComparison] = useState<VersionComparison | null>(null);
  const [comparingLoading, setComparingLoading] = useState(false);

  // Load history on open
  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await historyService.getHistory(entityType, entityId);
        setVersions(response.versions);

        // Default to comparing current (first) with previous (second)
        if (response.versions.length >= 2) {
          setSelectedVersion(response.versions[1].version);
        }
      } catch (error) {
        console.error('Failed to load history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open && entityId) {
      loadHistory();
    }
  }, [open, entityId, entityType]);

  // Load comparison when selected version changes
  useEffect(() => {
    const loadComparison = async () => {
      if (!selectedVersion || versions.length === 0) return;

      const currentVersion = versions[0]?.version;
      if (!currentVersion) return;

      setComparingLoading(true);
      try {
        const result = await historyService.compareVersions(
          entityType,
          entityId,
          selectedVersion,
          currentVersion
        );
        setComparison(result);
      } catch (error) {
        console.error('Failed to compare versions:', error);
      } finally {
        setComparingLoading(false);
      }
    };

    if (selectedVersion !== null && versions.length > 0) {
      loadComparison();
    }
  }, [selectedVersion, versions, entityType, entityId]);

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  const formatFieldName = (field: string): string => {
    // Convert camelCase to Title Case
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">{t('history.title')}</Typography>
            {entityName && (
              <Chip label={entityName} size="small" variant="outlined" />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : versions.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography color="text.secondary">
              {t('history.noHistory')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Version selector */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('history.compareWith')}</InputLabel>
                <Select
                  value={selectedVersion ?? ''}
                  onChange={e => setSelectedVersion(e.target.value as number)}
                  label={t('history.compareWith')}
                >
                  {versions.slice(1).map(v => (
                    <MenuItem key={v.version} value={v.version}>
                      {t('history.version', { version: v.version })} -{' '}
                      {formatDate(v.createdAt)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <CompareIcon color="action" />

              <Chip
                label={`${t('history.currentVersion')} (v${versions[0]?.version})`}
                color="primary"
                variant="outlined"
              />
            </Box>

            {/* Split comparison view */}
            {comparingLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={24} />
              </Box>
            ) : comparison ? (
              <Box display="flex" gap={2}>
                {/* Old version (left) */}
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.02),
                    borderColor: alpha(theme.palette.error.main, 0.2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="error.main"
                    gutterBottom
                  >
                    {t('history.version', {
                      version: comparison.version1.version,
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={2}
                  >
                    {comparison.version1.changedByUserName ||
                      t('common.unknown')}{' '}
                    • {formatDate(comparison.version1.createdAt)}
                  </Typography>
                  {comparison.version1.changeDescription && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {comparison.version1.changeDescription}
                    </Typography>
                  )}
                </Paper>

                {/* New version (right) */}
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.02),
                    borderColor: alpha(theme.palette.success.main, 0.2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="success.main"
                    gutterBottom
                  >
                    {t('history.version', {
                      version: comparison.version2.version,
                    })}{' '}
                    ({t('history.currentVersion')})
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={2}
                  >
                    {comparison.version2.changedByUserName ||
                      t('common.unknown')}{' '}
                    • {formatDate(comparison.version2.createdAt)}
                  </Typography>
                  {comparison.version2.changeDescription && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {comparison.version2.changeDescription}
                    </Typography>
                  )}
                </Paper>
              </Box>
            ) : null}

            {/* Changes table */}
            {comparison && comparison.changes.length > 0 && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('history.changes')} ({comparison.changes.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('history.field')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.error.main, 0.05),
                          }}
                        >
                          {t('history.oldValue')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.success.main, 0.05),
                          }}
                        >
                          {t('history.newValue')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.changes.map((change: FieldChange) => (
                        <TableRow key={change.field}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {formatFieldName(change.field)}
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.03),
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: 300,
                            }}
                          >
                            {formatValue(change.oldValue)}
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: alpha(theme.palette.success.main, 0.03),
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: 300,
                            }}
                          >
                            {formatValue(change.newValue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {comparison && comparison.changes.length === 0 && (
              <Box mt={3} textAlign="center" p={2}>
                <Typography color="text.secondary">
                  {t('history.noChanges')}
                </Typography>
              </Box>
            )}

            {/* Version list */}
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('history.title')} ({versions.length}{' '}
                {versions.length === 1 ? 'version' : 'versions'})
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {versions.map((v, index) => (
                  <Box
                    key={v.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1,
                      borderRadius: 1,
                      bgcolor:
                        index === 0
                          ? alpha(theme.palette.primary.main, 0.05)
                          : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Chip
                      label={`v${v.version}`}
                      size="small"
                      color={index === 0 ? 'primary' : 'default'}
                      variant={index === 0 ? 'filled' : 'outlined'}
                    />
                    <Box flex={1}>
                      <Typography variant="body2">
                        {v.changeDescription || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.changedByUserName || t('common.unknown')} •{' '}
                        {formatDate(v.createdAt)}
                      </Typography>
                    </Box>
                    {index === 0 && (
                      <Chip
                        label={t('history.currentVersion')}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryDialog;
